import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import DashboardService from 'api/dashboard/DashboardService';
import { FleetByAgencyDashboardDto } from 'api/dashboard/dtos/FleetByAgencyDashboardDto';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Box from 'common/components/box/Box';
import useInfiniteReveal from '../components/useInfiniteReveal';
import InlineKpiCounter from '../components/InlineKpiCounter';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import styles from './VehiclesByAgency.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

// Sentinel id used by vehicles filter screen to represent "no agency assigned".
const NONE_AGENCY_ID = '00000000-0000-0000-0000-000000000000';

const SCALE_COLORS = [
	'#025a7a', // darkest — top rank
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0', // lightest — 5th+
];

const VehiclesByAgency = () => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<FleetByAgencyDashboardDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const result = await DashboardService.getFleetByAgency({});
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, []);

	const noneLabel = t('dashboard.vehicles_by_agency_no_agency' as any) as string;

	const sorted = useMemo(() => [...(data?.items || [])].sort((a, b) => b.count - a.count), [data]);
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(sorted, PAGE_SIZE);

	if (!data && !isLoading) return null;

	if (!data?.items || data.items.length === 0) {
		return (
			<Box className={tableStyles.container} title={t('dashboard.vehicles_by_agency') as string} subTitleTooltip={t('dashboard.vehicles_by_agency_tooltip' as any) as string}>
				{isLoading
					? <div className={styles.centerAlign}><LoadingSpinner /></div>
					: <WidgetNoData />
				}
			</Box>
		);
	}

	const maxCount = sorted.length > 0 ? sorted[0].count : 0;
	const totalVehicles = data.totalVehicles;

	const goToVehiclesFiltered = (agencyId: string | null, agencyLabel: string) => {
		const id = agencyId ?? NONE_AGENCY_ID;
		try {
			// inactives: false — dashboard agrega só viaturas ativas; clique mantém o critério.
			const raw = localStorage.getItem('VEHICLESFILTER');
			const existing = raw ? JSON.parse(raw) : {};
			const next = {
				...existing,
				localIds: [id],
				localNames: [agencyLabel],
				localOperator: 'OR',
				inactives: false,
				page: 1,
				scrollToVehicleId: undefined,
			};
			localStorage.setItem('VEHICLESFILTER', JSON.stringify(next));
		} catch {
			// ignore
		}
		window.open('/vehicles', '_blank');
	};

	const goToAllVehicles = () => {
		try {
			localStorage.setItem('VEHICLESFILTER', JSON.stringify({ inactives: false }));
		} catch {
			// ignore
		}
		window.open('/vehicles', '_blank');
	};

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.vehicles_by_agency') as string}
			subTitleTooltip={t('dashboard.vehicles_by_agency_tooltip' as any) as string}
			renderOptions={<InlineKpiCounter value={String(totalVehicles)} subLabel={t('dashboard.total_assets' as any) as string} onClick={goToAllVehicles} />}
		>
			<div className={styles.chartContainer}>
				{isLoading ? <div className={styles.centerAlign}><LoadingSpinner /></div> : (
					<>
						<div className={styles.list}>
						{visibleItems.map((item, i) => {
							const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
							const color = SCALE_COLORS[Math.min(i, SCALE_COLORS.length - 1)];
							const label = item.agencyName || noneLabel;
							return (
								<div
									key={item.agencyId ?? 'none'}
									className={`${styles.row} ${styles.rowClickable}`}
									onClick={() => goToVehiclesFiltered(item.agencyId, label)}
								>
									<span className={styles.rank}>{i + 1}</span>
									<span className={styles.label}>{label}</span>
									<div className={styles.barTrack}>
										<div className={styles.barFill} style={{ width: `${barWidth}%`, background: color }} />
									</div>
									<span className={styles.value}>{item.count}</span>
								</div>
							);
						})}
						{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
						</div>
					</>
				)}
			</div>
		</Box>
	);
};

export default VehiclesByAgency;
